import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useInstalledApps, installedAppsQueryOptions } from '@/api/queries/useInstalledApps';
import { findInstalledApp } from '@/lib/app-registry';
import { getAppComponent, resolveAppTitle } from '@/lib/app-loader';
import { AppContextBridge } from '@/lib/app-context-bridge';
import { AppSkeleton } from '@bliks-io/app-sdk/react';
import { QueryErrorRetry } from '@/components/query-error-retry/QueryErrorRetry';
import i18next from '@/lib/i18n';

export const Route = createFileRoute('/$slug/apps/$')({
  loader: async ({ params, context }) => {
    const appId = params._splat?.split('/')[0] ?? '';
    let fallbackLabel;
    try {
      const apps = await context.queryClient.ensureQueryData(installedAppsQueryOptions);
      fallbackLabel = findInstalledApp(apps, appId)?.routes[0]?.label;
    } catch {
      /* fall through — resolveAppTitle uses appId as last resort */
    }
    return {
      breadcrumb: resolveAppTitle(appId, fallbackLabel, i18next.language),
      // App-internal navigation lives entirely in search params, so the app
      // crumb doubles as a link back to the app's start page.
      breadcrumbClearsSearch: true
    };
  },
  component: AppRoute
});

function AppRoute() {
  const { t } = useTranslation();
  const { _splat } = Route.useParams();
  const appId = _splat?.split('/')[0] ?? '';

  const { data: apps, isPending, isError, refetch } = useInstalledApps();
  const app = findInstalledApp(apps, appId);
  const AppComponent = app ? getAppComponent(app.appId) : null;

  if (isPending) return <AppSkeleton />;

  if (isError && !apps) return <QueryErrorRetry onRetry={refetch} className="h-full" />;

  if (!app || !AppComponent) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('common.Apps.notFound')}</p>
      </div>
    );
  }

  return <AppContextBridge app={app} AppComponent={AppComponent} />;
}
